// Mock conflict detection and validation functions
import { Conflict, ConflictType, Lesson, Room } from '../../types';
import { lessonsOverlap } from './timetableHelpers';

/**
 * Detect all conflicts in a timetable
 */
export function detectConflicts(lessons: Lesson[], rooms: Room[], studentGroups: Map<string, number>): Conflict[] {
  const conflicts: Conflict[] = [];

  // Check teacher double-bookings
  conflicts.push(...detectTeacherDoubleBookings(lessons));

  // Check room double-bookings
  conflicts.push(...detectRoomDoubleBookings(lessons));

  // Check capacity violations
  conflicts.push(...detectCapacityViolations(lessons, rooms, studentGroups));

  // Check student overlaps
  conflicts.push(...detectStudentOverlaps(lessons));

  return conflicts;
}

/**
 * Detect teacher double-booking conflicts
 */
function detectTeacherDoubleBookings(lessons: Lesson[]): Conflict[] {
  const conflicts: Conflict[] = [];
  const teacherLessons = new Map<string, Lesson[]>();

  // Group lessons by teacher
  lessons.forEach((lesson) => {
    const existing = teacherLessons.get(lesson.teacherId);
    if (existing) {
      existing.push(lesson);
    } else {
      teacherLessons.set(lesson.teacherId, [lesson]);
    }
  });

  // Check for overlaps
  teacherLessons.forEach((teacherLessonList, teacherId) => {
    for (let i = 0; i < teacherLessonList.length; i++) {
      for (let j = i + 1; j < teacherLessonList.length; j++) {
        if (lessonsOverlap(teacherLessonList[i], teacherLessonList[j])) {
          conflicts.push({
            id: `conflict-teacher-${teacherId}-${i}-${j}`,
            timetableId: teacherLessonList[i].timetableId,
            type: 'teacher-double-booking' as ConflictType,
            severity: 'high',
            affectedLessons: [teacherLessonList[i].id, teacherLessonList[j].id],
            affectedTeachers: [teacherId],
            message: `Teacher ${teacherId} is double-booked on ${teacherLessonList[i].day} period ${teacherLessonList[i].period}`,
            details: `Conflicts between ${teacherLessonList[i].subjectId} and ${teacherLessonList[j].subjectId}`,
            status: 'unresolved',
            detectedAt: new Date().toISOString(),
          });
        }
      }
    }
  });

  return conflicts;
}

/**
 * Detect room double-booking conflicts
 */
function detectRoomDoubleBookings(lessons: Lesson[]): Conflict[] {
  const conflicts: Conflict[] = [];
  const roomLessons = new Map<string, Lesson[]>();

  // Group lessons by room
  lessons.forEach((lesson) => {
    const existing = roomLessons.get(lesson.roomId);
    if (existing) {
      existing.push(lesson);
    } else {
      roomLessons.set(lesson.roomId, [lesson]);
    }
  });

  // Check for overlaps
  roomLessons.forEach((roomLessonList, roomId) => {
    for (let i = 0; i < roomLessonList.length; i++) {
      for (let j = i + 1; j < roomLessonList.length; j++) {
        if (lessonsOverlap(roomLessonList[i], roomLessonList[j])) {
          conflicts.push({
            id: `conflict-room-${roomId}-${i}-${j}`,
            timetableId: roomLessonList[i].timetableId,
            type: 'room-double-booking' as ConflictType,
            severity: 'medium',
            affectedLessons: [roomLessonList[i].id, roomLessonList[j].id],
            affectedRooms: [roomId],
            message: `Room ${roomId} is double-booked on ${roomLessonList[i].day} period ${roomLessonList[i].period}`,
            status: 'unresolved',
            detectedAt: new Date().toISOString(),
          });
        }
      }
    }
  });

  return conflicts;
}

/**
 * Detect capacity violation conflicts
 */
function detectCapacityViolations(lessons: Lesson[], rooms: Room[], studentGroups: Map<string, number>): Conflict[] {
  const conflicts: Conflict[] = [];
  const roomMap = new Map(rooms.map((r) => [r.id, r]));

  lessons.forEach((lesson) => {
    const room = roomMap.get(lesson.roomId);
    const studentCount = studentGroups.get(lesson.studentGroupId) || lesson.studentCount;

    if (room && studentCount > room.capacity) {
      conflicts.push({
        id: `conflict-capacity-${lesson.id}`,
        timetableId: lesson.timetableId,
        type: 'capacity-violation' as ConflictType,
        severity: 'medium',
        affectedLessons: [lesson.id],
        affectedRooms: [lesson.roomId],
        message: `Room ${room.name} capacity (${room.capacity}) exceeded by ${studentCount} students`,
        details: `${studentCount - room.capacity} students over capacity`,
        status: 'unresolved',
        detectedAt: new Date().toISOString(),
      });
    }
  });

  return conflicts;
}

/**
 * Detect student overlap conflicts
 */
function detectStudentOverlaps(lessons: Lesson[]): Conflict[] {
  const conflicts: Conflict[] = [];
  const groupLessons = new Map<string, Lesson[]>();

  // Group lessons by student group
  lessons.forEach((lesson) => {
    const existing = groupLessons.get(lesson.studentGroupId);
    if (existing) {
      existing.push(lesson);
    } else {
      groupLessons.set(lesson.studentGroupId, [lesson]);
    }
  });

  // Check for overlaps
  groupLessons.forEach((groupLessonList, groupId) => {
    for (let i = 0; i < groupLessonList.length; i++) {
      for (let j = i + 1; j < groupLessonList.length; j++) {
        if (lessonsOverlap(groupLessonList[i], groupLessonList[j])) {
          conflicts.push({
            id: `conflict-students-${groupId}-${i}-${j}`,
            timetableId: groupLessonList[i].timetableId,
            type: 'student-overlap' as ConflictType,
            severity: 'high',
            affectedLessons: [groupLessonList[i].id, groupLessonList[j].id],
            affectedStudents: [groupId],
            message: `Student group ${groupId} has overlapping lessons on ${groupLessonList[i].day} period ${groupLessonList[i].period}`,
            status: 'unresolved',
            detectedAt: new Date().toISOString(),
          });
        }
      }
    }
  });

  return conflicts;
}

/**
 * Validate a lesson placement
 */
export function validateLessonPlacement(
  lesson: Lesson,
  existingLessons: Lesson[],
  rooms: Room[],
  studentGroups: Map<string, number>,
): { valid: boolean; conflicts: Conflict[] } {
  const testLessons = [...existingLessons, lesson];
  const conflicts = detectConflicts(testLessons, rooms, studentGroups).filter((c) =>
    c.affectedLessons.includes(lesson.id),
  );

  return {
    valid: conflicts.length === 0,
    conflicts,
  };
}

/**
 * Get conflict severity color
 */
export function getConflictSeverityColor(severity: 'high' | 'medium' | 'low'): string {
  const colors = {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#3b82f6',
  };
  return colors[severity];
}

/**
 * Enhanced conflict detection for timetable-level analysis
 */
export interface ConflictCheckResult {
  conflicts: any[];
  totalConflicts: number;
  highSeverity: number;
  mediumSeverity: number;
  lowSeverity: number;
}

export class ConflictDetector {
  private lessons: Lesson[];
  private rooms: Room[];

  constructor(lessons: Lesson[], rooms: Room[]) {
    this.lessons = lessons;
    this.rooms = rooms;
  }

  public detectAllConflicts(): ConflictCheckResult {
    const studentGroups = new Map<string, number>();
    // Populate from lessons
    this.lessons.forEach((lesson) => {
      if (lesson.studentCount && !studentGroups.has(lesson.studentGroupId)) {
        studentGroups.set(lesson.studentGroupId, lesson.studentCount);
      }
    });

    const conflicts = detectConflicts(this.lessons, this.rooms, studentGroups);

    const highSeverity = conflicts.filter((c) => c.severity === 'high').length;
    const mediumSeverity = conflicts.filter((c) => c.severity === 'medium').length;
    const lowSeverity = conflicts.filter((c) => c.severity === 'low').length;

    return {
      conflicts,
      totalConflicts: conflicts.length,
      highSeverity,
      mediumSeverity,
      lowSeverity,
    };
  }

  public getLessonConflicts(lessonId: string): Conflict[] {
    const result = this.detectAllConflicts();
    return result.conflicts.filter((c) => c.affectedLessons.includes(lessonId));
  }

  public getSlotConflicts(day: string, period: number): Conflict[] {
    const result = this.detectAllConflicts();
    return result.conflicts.filter((c) => {
      // Check if any affected lesson is in this slot
      const affectedInSlot = this.lessons.filter(
        (l) => c.affectedLessons.includes(l.id) && l.day === day && l.period === period,
      );
      return affectedInSlot.length > 0;
    });
  }
}
